/** Derivarea machetei de situație: din stratul de situație, o scenă de extrudat.
 *
 *  ACELAȘI STRAT DE DERIVARE CA PLANȘA, nu un al doilea. Copacii, locurile de
 *  parcare și rampele ies din `siteDerive` — exact funcțiile pe care le cheamă
 *  `SitePlanView` ca să deseneze A01, cu aceleași argumente. Motivul e simplu:
 *  dacă macheta și-ar împrăștia singură copacii, cele două vederi ale aceleiași
 *  soluții ar arăta plantații diferite, iar omul care comută între ele ar avea
 *  dreptate să nu creadă niciuna.
 *
 *  CE ADAUGĂ derivarea asta e a treia dimensiune, și atât: o cotă pentru fiecare
 *  suprafață și o înălțime pentru fiecare corp. Nimic nu se inventează — un corp
 *  fără înălțime în document nu primește una plauzibilă aici, ci e sărit, fiindcă
 *  din înălțimile astea iese verdictul de însorire.
 *
 *  CADRUL e cel al desenului, adică deja trecut prin `canonical.northUp`: +x est,
 *  +y SUD, +z sus. V. nota lungă de la `vectorSoare`. */

import type { SiteLayer, SiteShape } from "../client";
import type { Pt } from "../types";
import {
  copaciiDocumentului, holesOf, inelCuGauri, marcajeleSitului, obstacolePlantatie, plantatiaLotului, ringOf, siteRamps,
  siteStalls,
  type DungaZebra, type SiteRamp, type SiteStall,
} from "./siteDerive";
import type { Sageata } from "./carosabil";
import type { Volum } from "./insorire";
import type { StoreyPlan, PanouFatada, ParapetFatada, TamplarieGol } from "../canonical";
import { segmentulGolului } from "./arh3d";
import { borduri } from "./borduri";
import { boschetiInPalcuri, Obstacole, tufisuriDupaParcari, type Palc } from "./vegetatie";
import type { ZonaSit } from "./zone3d";

/** O suprafață la sol: un inel, cu golurile lui. */
export interface Suprafata { poly: number[][]; holes: number[][][] }

export interface Corp3 {
  id: string;
  eticheta: string;
  pts: number[][];
  /** Înălțimea la atic, în metri. */
  h: number;
  /** BUCĂȚILE corpului, când urcă diferit (v. `SiteBar.trepte`): corpul se
   *  ridică bucată cu bucată, fiecare la înălțimea ei. Lipsă = o singură cutie. */
  trepte?: { pts: number[][]; h: number }[];
}

export interface Copac3 { x: number; y: number; r: number }

/** Cât iese casa scării de evacuare din teren: un nivel de parcaj (2,50 m între
 *  plăci, `architecture/inaltimi.py`) plus copertina ieșirii. */
export const INALTIME_SCARA_EVACUARE = 2.8;

/** PARTERUL STĂ LA TEREN? Terenul machetei stă la `-ridicare` sub ±0,00 (soclul
 *  blocurilor, cel mai mare de pe sit); un parter comercial sau un corp de
 *  funcțiune e scris cu pardoseala chiar acolo (−0,90). Un asemenea parter n-are
 *  soclu de piatră, iar pardoseala scobiturii lui de intrare E terenul: o placă
 *  desenată acolo stă în planul terenului și pâlpâie cu el (Pipera, 4.11.2026,
 *  800 px la 13 m). Toleranța: zgomotul de rotunjire al cotelor, nu o treaptă. */
export function parterLaTeren(cota: number, ridicare: number): boolean {
  return cota <= -ridicare + 0.05;
}

export interface ScenaSit {
  /** SOCLUL: cât stă ±0,00 peste teren (v. `SiteLayer.ridicare`). Terenul și
   *  tot ce e pe el se desenează la `-ridicare`; corpurile pornesc de acolo. */
  ridicare: number;
  lot: number[][];
  corpuri: Corp3[];
  verde: Suprafata[];
  verdePeSlab: Suprafata[];
  trotuare: Suprafata[];
  drumuri: Suprafata[];
  locuri: SiteStall[];
  rampe: SiteRamp[];
  /** CASELE DE SCARĂ DE EVACUARE ale parcajului care ies la teren (§35): cutii
   *  mici la sol, înalte cât un nivel de parcaj, pe conturul casei de scară. */
  scari: { poly: number[][]; h: number; id: string }[];
  /** ZONELE EXTERIOARE (3.18): platformele de gunoi, locurile de joacă și
   *  piațetele, cu poligonul planșei; macheta le mobilează (`zone3d.ts`). */
  zone: ZonaSit[];
  copaci: Copac3[];
  /** Boscheții: la baza pomilor și presărați pe verde (v. `vegetatie.ts`). */
  tufisuri: Copac3[];
  /** Marcajele carosabilului: axele punctate dintre benzi, polilinii în metri
   *  (`siteDerive.marcajeleSitului`, aceleași ca pe planșă). */
  marcaje: number[][][];
  /** Săgețile de sens ale aleilor cu sens unic: centru și heading (convenția
   *  glifei de pe planșă, `RoadArrowMark`). */
  sageti: Sageata[];
  /** Dungile zebrelor trecerilor de pietoni, patrulatere în metri, pe asfalt. */
  zebre: DungaZebra[];
  /** Bordurile: muchiile exterioare ale asfaltului luat împreună (drumuri,
   *  rampe, locuri de parcare), polilinii în metri (v. `borduri.ts`). */
  borduri: number[][][];
  /** Ce face umbră, în forma cerută de studiul de însorire. */
  volume: Volum[];
  /** Golurile reale, când documentul de arhitectură e disponibil. Gol când nu
   *  e — și atunci volumele rămân curate, fără fațadă inventată. */
  fatade: Fatada3[];
  /** Gabaritul scenei — cadrul, centrul și raza, pentru încadrarea camerei. */
  cadru: { minX: number; minY: number; maxX: number; maxY: number;
           cx: number; cy: number; raza: number };
}

const supr = (sh: SiteShape): Suprafata | null => {
  const poly = ringOf(sh);
  return poly.length >= 3 ? { poly, holes: holesOf(sh).filter((h) => h.length >= 3) } : null;
};
const suprafete = (list: SiteShape[] | undefined): Suprafata[] =>
  (list ?? []).map(supr).filter((s): s is Suprafata => !!s);

/** Poligonul unui drum. Stratul îl scrie în două feluri — inel simplu sau obiect
 *  cu `poly` — iar dintr-o axă fără poligon nu se poate extrage o suprafață. */
function drumPoly(r: SiteLayer["roads"][number]): Suprafata | null {
  if (Array.isArray(r)) return r.length >= 3 ? { poly: r, holes: [] } : null;
  const p = r?.poly;
  // găurile (curtea unei alei-buclă) cusute în inel: obstacolele boscheților și
  // marcajele primesc inele simple, ca pe planșă (`roadRing`)
  return p && p.length >= 3 ? { poly: r.holes?.length ? inelCuGauri(p, r.holes) : p, holes: [] } : null;
}

export function scenaSit(site: SiteLayer | null | undefined,
                         lot: Pt[] | null | undefined,
                         niveluri?: readonly StoreyPlan[] | null): ScenaSit | null {
  if (!site) return null;

  const corpuri: Corp3[] = [];
  (site.bars ?? []).forEach((b, i) => {
    const pts = (b?.pts ?? []).filter(
      (p) => Number.isFinite(p?.[0]) && Number.isFinite(p?.[1]));
    // UN CORP FĂRĂ ÎNĂLȚIME E SĂRIT, nu ridicat la o cotă plauzibilă. Din cotele
    // astea iese studiul de însorire: un corp inventat la 12 m ar produce un
    // verdict inventat, iar verdictul ăla se citește ca un fapt.
    if (pts.length < 3 || !Number.isFinite(b?.h) || !(b.h > 0)) return;
    const trepte = (b.trepte ?? [])
      .map((t) => ({ pts: (t.pts ?? []).filter((p) => Number.isFinite(p?.[0]) && Number.isFinite(p?.[1])), h: t.h }))
      .filter((t) => t.pts.length >= 3 && Number.isFinite(t.h) && t.h > 0);
    corpuri.push({
      id: b.code || `bar${i}`,
      eticheta: b.label || b.code || `Corp ${i + 1}`,
      pts, h: b.h,
      ...(trepte.length >= 2 ? { trepte } : {}),
    });
  });

  // ACELEAȘI argumente ca în `SitePlanView`: aceeași plantație pe amândouă
  // vederile — pomii în pâlcuri (v. `vegetatie.ts`); macheta mai pune boscheții
  // pâlcurilor și șirul din spatele parcărilor.
  const plantat = plantatiaLotului(site.green, obstacolePlantatie(site), lot ?? null,
                                   copaciiDocumentului(site.trees));
  const copaci: Copac3[] = plantat.copaci.map((t) => ({ x: t.x, y: t.y, r: t.r }));

  const lotPts = (lot ?? []).filter(
    (p) => Number.isFinite(p?.[0]) && Number.isFinite(p?.[1])) as number[][];

  const verde = suprafete(site.green?.deep);
  const verdePeSlab = suprafete(site.green?.over_slab);
  const trotuare = suprafete(site.sidewalks);
  const drumuri = (site.roads ?? [])
    .map(drumPoly).filter((s): s is Suprafata => !!s);
  const zone: ZonaSit[] = (site.zones ?? [])
    .filter((z) => (z?.poly?.length ?? 0) >= 3)
    .map((z, i) => ({ kind: z.kind, poly: z.poly, id: z.agent ?? `zona${i + 1}` }));

  // Cadrul se ia din TOT ce se desenează, nu doar din lot: un document de import
  // n-are lot, iar unul cu acces din drum are geometrie și dincolo de limită.
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const vezi = (p: number[]) => {
    if (!Number.isFinite(p?.[0]) || !Number.isFinite(p?.[1])) return;
    if (p[0] < minX) minX = p[0];
    if (p[0] > maxX) maxX = p[0];
    if (p[1] < minY) minY = p[1];
    if (p[1] > maxY) maxY = p[1];
  };
  lotPts.forEach(vezi);
  corpuri.forEach((c) => c.pts.forEach(vezi));
  [...verde, ...verdePeSlab, ...trotuare, ...drumuri, ...zone].forEach((s) => s.poly.forEach(vezi));
  if (!Number.isFinite(minX)) return null;

  const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2;
  const fatade = niveluri?.length
    ? fatadeDinNiveluri(niveluri, corpuri.map((c) => c.pts)) : [];
  // CORPUL AJUNGE PÂNĂ LA TAVANUL ULTIMULUI ETAJ. Planul de situație dă corpului
  // înălțimea TERASEI (h_terasa, cu 1,50 sub atic: 34,5 la P+11), iar
  // arhitectura stivuiește 12 etaje de 3,00 = 36. Cu corpul la 34,5, ultimul
  // etaj ieșea 1,5 m peste acoperiș — pielea de fațadă fără nimic în spate —
  // sau, tăiat la corp, rămânea fără jumătate de fereastră. Când arhitectura
  // e aici, ea știe mai bine unde se termină blocul: corpul se ridică până la
  // planșeul de peste ultimul etaj, iar placa terasei stă acolo.
  for (const f of fatade) {
    const c = corpuri[f.corp];
    if (!c || f.acoperis) continue;
    const sus = f.cota + f.inaltime;
    if (sus > c.h + 0.05 && sus < c.h + 6) c.h = sus;
  }
  // …ȘI FIECARE BUCATĂ a unui corp cu înălțimi diferite, la tavanul ULTIMULUI
  // EI etaj: nivelul al cărui planșeu de jos e sub înălțimea ei și al cărui
  // tavan e cel mai de sus (v. `Corp3.trepte`).
  corpuri.forEach((c, ci) => {
    for (const t of c.trepte ?? []) {
      const tavan = Math.max(0, ...fatade
        .filter((f) => f.corp === ci && !f.acoperis && f.cota < t.h - 0.05)
        .map((f) => f.cota + f.inaltime));
      if (tavan > t.h + 0.05 && tavan < t.h + 6) t.h = tavan;
    }
  });
  const locuri = siteStalls(site.stalls);
  const rampe = siteRamps((site as { ramps?: { poly: number[][]; slope?: number;
                                               mouth?: number[][] }[] }).ramps);
  const marcaje = marcajeleSitului(site, lotPts);
  return {
    ridicare: Math.max(0, site.ridicare ?? 0),
    lot: lotPts,
    corpuri, verde, verdePeSlab, trotuare, drumuri, copaci,
    marcaje: marcaje.linii, sageti: marcaje.sageti, zebre: marcaje.zebre,
    borduri: borduri([...drumuri, ...rampe.map((r) => ({ poly: r.poly })),
                      ...locuri.map((l) => ({ poly: dreptunghiulLocului(l) }))], lotPts),
    tufisuri: tufisuriLotului(corpuri, verde, verdePeSlab, trotuare, drumuri, locuri, rampe, copaci, plantat.palcuri),
    locuri, rampe,
    scari: (site.scariEvacuare ?? []).filter((s) => s.contur.length >= 3)
      .map((s) => ({ poly: s.contur, h: INALTIME_SCARA_EVACUARE, id: s.id })),
    zone,
    // CE FACE UMBRĂ: bucată cu bucată pe un corp cu înălțimi diferite — o
    // cutie la regim ar umbri cu etaje care nu există.
    volume: corpuri.flatMap((c) => (c.trepte?.length
      ? c.trepte.map((t, k) => ({ pts: t.pts, h: t.h, id: `${c.eticheta}/${k + 1}` }))
      : [{ pts: c.pts, h: c.h, id: c.eticheta }])),
    fatade,
    cadru: { minX, minY, maxX, maxY, cx, cy,
             raza: Math.max(8, Math.hypot(maxX - minX, maxY - minY) / 2) },
  };
}

/** Dreptunghiul unui loc de parcare, în lume (centru, rotire, lățime, lungime). */
export function dreptunghiulLocului(l: SiteStall): number[][] {
  const a = (l.deg * Math.PI) / 180, c = Math.cos(a), s = Math.sin(a);
  return [[-1, -1], [1, -1], [1, 1], [-1, 1]].map(([u, v]) =>
    [l.cx + c * (u * l.w / 2) - s * (v * l.d / 2), l.cy + s * (u * l.w / 2) + c * (v * l.d / 2)]);
}

/** Boscheții lotului (v. `vegetatie.ts`): gardul viu și tufele PÂLCURILOR de
 *  pomi, și șirul din spatele locurilor de parcare. Nu pe clădiri (cu un
 *  metru de margine, cât să nu crească în fațadă), nu pe trotuare, alei,
 *  parcări sau rampe, nu în trunchiul unui pom. */
function tufisuriLotului(corpuri: Corp3[], verde: Suprafata[], verdePeSlab: Suprafata[],
                         trotuare: Suprafata[], drumuri: Suprafata[], locuri: SiteStall[],
                         rampe: SiteRamp[], copaci: Copac3[], palcuri: Palc[]): Copac3[] {
  if (!verde.length && !verdePeSlab.length) return [];
  const obst = new Obstacole(20);
  for (const c of corpuri) {
    for (const t of c.trepte?.length ? c.trepte : [{ pts: c.pts }]) obst.inel(t.pts, 1.0);
  }
  for (const s of [...trotuare, ...drumuri]) obst.inel(s.poly, 0.2);
  for (const r of rampe) obst.inel(r.poly, 0.3);
  for (const l of locuri) obst.cerc(l.cx, l.cy, Math.hypot(l.w, l.d) / 2);
  for (const c of copaci) obst.cerc(c.x, c.y, 0.35);
  return [
    ...boschetiInPalcuri(palcuri, [...verde, ...verdePeSlab], obst, { pas: 2.6, sare: 31 }),
    ...tufisuriDupaParcari(locuri, drumuri.map((d) => d.poly), [...verde, ...verdePeSlab], obst,
                           { pas: 1.0, retras: 0.7, sare: 37 }),
  ];
}

// ---------------------------------------------------------------------------
// FAȚADELE REALE
//
// Macheta desena, până acum, o bandă de vitraj pe fiecare nivel, dedusă din
// înălțime. Era o INVENȚIE: nimic din document nu spunea că fereastra e acolo,
// și pe un P+16 ieșeau șaptesprezece dungi care nu corespundeau cu niciun gol.
// O fațadă plauzibilă și falsă e mai rea decât un volum curat, fiindcă nu se
// vede că e falsă.
//
// Golurile ADEVĂRATE nu stau în documentul de situație — acolo n-a existat
// niciodată `Window` — ci în cel de arhitectură, care conține și stratul de
// situație, și interioarele, în ACELAȘI sistem de coordonate. De acolo vin.


/** Un gol pe fațadă: segmentul lui la sol, cotele pe verticală, și ÎNCOTRO dă.
 *
 *  Normala se calculează AICI, din conturul planșeului care deține golul — nu
 *  în randor, din amprenta corpului. Cele două nu se pot lega: `StoreyPlan.cod`
 *  și `SiteBar.code` sunt vocabulare diferite (unul e codul corpului din
 *  arhitectură, celălalt codul de pe planșă, care pe documentele generate
 *  lipsește cu totul). Împerecheate după nume, două corpuri din trei nimereau
 *  inelul greșit, normala ieșea pe dos, iar dreptunghiul era eliminat la
 *  randare ca față din spate — adică ferestrele pur și simplu nu apăreau. */
export interface GolFatada {
  a: Pt; b: Pt; parapet: number; inaltime: number;
  /** Versorul care iese din clădire, în plan. */
  n: Pt;
  /** Fereastră sau ușă (de balcon, de terasă): ușa n-are glaf, are prag și
   *  foaie cu traversă lată jos. */
  tip?: "Window" | "Door";
  /** Tâmplăria din document: canate, lățimea profilului și culoarea tocului;
   *  lipsă = convenția machetei. */
  canate?: number;
  profil?: number;
  culoare?: string;
  /** …și toată secțiunea ei, când fațada a scris-o (v. `tamplarie3d`). */
  tamplarie?: TamplarieGol;
  /** Id-ul golului în document (`Window`/`Door`), pentru selecția de pe machetă. */
  eid?: string;
  /** La ușă: în care toc e balamaua (`end` = capătul `b`). */
  balama?: "start" | "end";
  /** FRONT VITRAT CONTINUU: golul vecin de la capătul `a` / `b` e la atâtea
   *  metri (≤ 0,12). Pe partea aceea tocul n-are stâlp și nici șpalet, geamul
   *  merge până la margine, iar între cele două stă UN montant (desenat de
   *  golul din stânga) — nu două rame spate în spate cu zidărie între ele. */
  lipitA?: number;
  lipitB?: number;
  /** GEAMUL PE COLȚ: id-ul golului de pe fața vecină (din document) și, aflat
   *  la randare, capătul din colț (`la`), linia feței vecine pe `x`-ul golului
   *  cu colțul adus la `b` (`x`), și dacă golul ăsta desenează montantul de
   *  colț (`stapan` — unul singur din pereche). */
  coltCu?: string;
  colt?: { la: "a" | "b"; x: number; stapan: boolean };
}

/** Un nivel al unui corp, cu tot ce se vede din el de afară.
 *
 *  `corp` e INDEXUL în `corpuri`, nu un cod. Planșeele arhitecturii și barele
 *  planșei de situație se numesc diferit, deci legătura se face prin geometrie:
 *  planșeul aparține corpului în a cărui amprentă îi cade centrul. Un nume
 *  potrivit greșit mută un etaj întreg pe altă clădire. */
export interface Fatada3 {
  corp: number;
  cota: number;
  /** Planșeul și numărul lui (`Storey.properties.level`): zona aleasă pe machetă
   *  spune etajele din ele. */
  storey?: string;
  nivel?: number;
  /** Id-urile din document ale balcoanelor (`Space`), în ordinea din `balcoane`. */
  balcoaneEid?: string[];
  /** CONTURUL PLANȘEULUI nivelului, din arhitectură: pe el se ridică volumul
   *  corpului la nivelul ăsta — nu pe bucata planului de situație, care la un
   *  L sau la o treaptă trece dincolo de fațadă și îngroapă ferestrele de sub
   *  terasă, iar deasupra terasei lasă un cub (Test-Catalin-v1, 2.10.2026). */
  contur?: number[][];
  /** PLĂCILE nivelului, când planșeul stă în mai multe bucăți (`StoreyPlan.parts`):
   *  corpul se ridică pe fiecare, iar `contur` e doar cea mai mare. */
  parti?: number[][][];
  goluri: GolFatada[];
  /** Balcoanele ÎN CONSOLĂ ale nivelului, ca inele — din `Space` cu
   *  `kind: "balcony"` și `outdoor` diferit de `loggia`. Placa lor iese din corp. */
  balcoane: number[][][];
  /** LOGIILE nivelului: scobituri în volum. Corpul se decupează pe conturul lor,
   *  iar pielea feței primește un gol pe lățimea lor. */
  logii: number[][][];
  /** PLĂCILE ÎN CONSOLĂ fără balcon pe ele (`Slab` de fel `cantilever`, format
   *  3.5): tavanul unei cutii de balcon de dedesubt, pus de compoziție acolo
   *  unde deasupra nu e balcon. `cota` = fața de sus, peste pardoseala
   *  nivelului (0 de obicei; înălțimea etajului pe ultimul). */
  copertine?: { poly: number[][]; cota: number; structura?: string; grosime?: number }[];
  /** SCOBITURILE INTRĂRILOR de la parter (`Space` `intrare_acoperita`, 3.19):
   *  sunt și în `logii` — corpul se scobește, pielea feței primește gura —,
   *  dar pardoseala lor e a lor (la teren, `denivelate`), nu placa logiei. */
  intrari?: number[][][];
  /** SPOTURILE intrării: în tavanul scobiturii și sub copertină, cu `z` față
   *  de pardoseala nivelului (fața de jos a tavanului sau a copertinei). */
  lumini?: { x: number; y: number; z: number }[];
  /** PLĂCILE LA ALTĂ COTĂ ale nivelului (`Slab` de fel `offset`, 3.12): zone
   *  ale planșeului cu pardoseala la `cota` față de pardoseala nivelului —
   *  holul de intrare coborât la teren (−0,90). Soclul se scobește pe ele,
   *  iar pardoseala lor se desenează la cota ei. */
  denivelate?: { poly: number[][]; cota: number; eid?: string }[];
  /** TREPTELE dintre două cote ale nivelului (`Stair`, 3.12): amprenta, sensul
   *  urcării (`deg`), de unde pornesc (`cota0`, față de pardoseala nivelului),
   *  cât urcă (`h`) și câte contratrepte. */
  trepte?: { poly: number[][]; deg: number; cota0: number; h: number; contratrepte: number; eid?: string }[];
  /** PLATFORMA ELEVATOARE de lângă trepte (`Lift`, 3.12). */
  platforme?: { poly: number[][]; cota0: number; h: number; eid?: string }[];
  /** TERASELE ultimului etaj (retragerea): sunt și în `logii` — corpul se
   *  scobește pe ele, placa lor se desenează — dar pielea fațadei NU primește
   *  gaură pe lățimea lor: parapetul lor de pe contur e o față a fațadei
   *  (`_pt`), cu panourile lui, iar gaura îl ștergea și rămânea zidul gol,
   *  alb, lângă lemn (28.09.2026). */
  terase: number[][][];
  /** Nivelul de TERASĂ (`storey_kind: roof`), când documentul are unul. */
  acoperis: boolean;
  /** Înălțimea aticului PESTE acest nivel, din panourile `parapet` de pe el —
   *  aticul și terasa stau pe ultimul etaj, cu `z` peste înălțimea lui. 0 pe un
   *  etaj curent. */
  atic: number;
  /** Panourile de fațadă ale nivelului, cu normala feței rezolvată; și
   *  balustradele. Goale pe un document fără fațadă. */
  panouri: PanouFatada[];
  parapete: ParapetFatada[];
  /** Înălțimea nivelului (până la planșeul următor). Cu ea corpul se ridică
   *  până la tavanul ultimului etaj — v. `scenaSit`. */
  inaltime: number;
  /** INELELE ATICULUI, din pereții `parapet` ai nivelului (inel cu gaură):
   *  ele urmează retragerea ultimului etaj (terasele), pe când amprenta
   *  corpului nu. Goale când documentul n-are un asemenea perete. */
  atic_inele: Suprafata[];
  /** PARAPETELE DE LA PARDOSEALĂ ale nivelului (pereți `parapet` fără `sill_m`):
   *  zidurile joase din jurul teraselor ultimului etaj și dintre ele, puse de
   *  `layout.terasa`. Se desenează ca ziduri de la pardoseală, nu ca atic. */
  parapete_zid: { poly: number[][]; holes: number[][][]; h: number }[];
}

/** Parapetul unei ferestre când documentul nu-l scrie. Aceeași valoare ca în
 *  `arh3d.goluriNivel`, ca cele două machete să nu arate ferestre la înălțimi
 *  diferite pe aceeași clădire. */
const PARAPET = 0.85;

/** Plafonul înălțimii de gol. `height_m` vine `null` pe documentele generate,
 *  deci înălțimea E O CONVENȚIE — poziția și lățimea ferestrei sunt reale, ea nu.
 *
 *  Convenția se sprijine totuși pe ce scrie documentul: se ia din distanța dintre
 *  planșee, așa că un parter comercial de 4,5 m primește un gol mai înalt decât
 *  un etaj de 2,9. Cu un plafon fix de 1,60 toate nivelurile ieșeau identice, iar
 *  regimul real al clădirii — care E în document — se pierdea pe drum. */
const GOL_MAX = 2.4;

/** Cât de aproape de conturul nivelului trebuie să cadă un gol ca să fie pe
 *  FAȚADĂ. Ferestrele stau pe pereții exteriori; ce e mai înăuntru e un gol
 *  interior și n-are ce căuta pe silueta văzută de afară. */
const LANGA_CONTUR = 0.9;

function distLaSegment(px: number, py: number, a: number[], b: number[]): number {
  const dx = b[0] - a[0], dy = b[1] - a[1];
  const L2 = dx * dx + dy * dy;
  if (!L2) return Math.hypot(px - a[0], py - a[1]);
  let t = ((px - a[0]) * dx + (py - a[1]) * dy) / L2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (a[0] + dx * t), py - (a[1] + dy * t));
}

/** Distanța de la un punct la conturul nivelului. */
function distLaContur(px: number, py: number, ring: number[][]): number {
  let d = Infinity;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const q = distLaSegment(px, py, ring[j], ring[i]);
    if (q < d) d = q;
  }
  return d;
}

/** Punctul e în poligon? Trasare de rază. */
/** Distanța de la un punct la DREAPTA laturii alese (prin `p`, direcția `u`):
 *  cât de departe stă golul de peretele pe care ar fi așezat. */
function distLaSegmentDreapta(px: number, py: number, p: Pt, u: Pt): number {
  return Math.abs(-(px - p[0]) * u[1] + (py - p[1]) * u[0]);
}

function inPoligon(x: number, y: number, ring: number[][]): boolean {
  let c = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i], [xj, yj] = ring[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) c = !c;
  }
  return c;
}

/** Latura conturului cea mai apropiată de un punct: unde cade proiecția lui pe
 *  ea, încotro merge latura, și încotro iese din clădire.
 *
 *  DE CE PROIECȚIA, nu poziția golului. O fereastră stă pe AXA peretelui, adică
 *  în medie la 23 cm în interiorul amprentei care se extrudează. Desenată acolo,
 *  dreptunghiul ei rămâne ÎNĂUNTRUL solidului — invizibil, oricâte s-ar genera.
 *  Pe fațadă se vede pielea clădirii, deci golul se așază pe ea: se proiectează
 *  pe latură și păstrează lățimea reală, centrată pe proiecție. */
/** Latura pe care stă golul.
 *
 *  APROAPE NU E DE AJUNS. Un gol al terasei stă pe zidul de închidere, de pe
 *  linia de retragere; dar colțul terasei aduce o a doua latură — parapetul —
 *  la fel de aproape sau mai aproape, iar lipit de ea ușa se muta pe parapet,
 *  cu 45 cm până la 1,25 m față de zidul ei (măsurat pe documentul
 *  beneficiarului, 23.09.2026: 28 de goluri din 88). Cu direcția golului
 *  (`dir`) se alege întâi latura PARALELĂ cu el, și abia între cele paralele
 *  cea mai apropiată — un gol nu stă niciodată de-a curmezișul zidului lui. */
function laturaApropiata(px: number, py: number, ring: number[][],
                         dir?: Pt | null): { p: Pt; u: Pt; n: Pt } | null {
  let best = Infinity, bestPar = -1, out: { p: Pt; u: Pt; n: Pt } | null = null;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const ax = ring[j][0], ay = ring[j][1];
    const bx = ring[i][0], by = ring[i][1];
    const dx = bx - ax, dy = by - ay, L2 = dx * dx + dy * dy;
    if (!L2) continue;
    const d = distLaSegment(px, py, ring[j], ring[i]);
    const L0 = Math.sqrt(L2);
    // 1 = latura merge în direcția golului, 0 = e perpendiculară pe el
    const par = dir ? Math.abs((dx / L0) * dir[0] + (dy / L0) * dir[1]) : 1;
    const paralela = par >= 0.7;
    const maiBuna = paralela !== (bestPar >= 0.7) ? paralela : d < best;
    if (!maiBuna) continue;
    best = d; bestPar = par;
    const L = Math.sqrt(L2);
    let t = ((px - ax) * dx + (py - ay) * dy) / L2;
    t = Math.max(0, Math.min(1, t));
    out = { p: [ax + dx * t, ay + dy * t], u: [dx / L, dy / L], n: [dy / L, -dx / L] };
  }
  if (!out) return null;
  if (inPoligon(out.p[0] + out.n[0] * 0.05, out.p[1] + out.n[1] * 0.05, ring)) {
    out.n = [-out.n[0], -out.n[1]];
  }
  return out;
}

/** Fațadele, din planșeele documentului de arhitectură.
 *
 *  Numai nivelurile SUPRATERANE cu cotă scrisă: un subsol n-are fațadă văzută,
 *  iar un planșeu fără cotă n-are unde să fie pus — și o cotă ghicită ar muta
 *  ferestrele pe alt etaj. */
/** Centrul unui inel. */
function centruInel(ring: number[][]): Pt {
  let x = 0, y = 0;
  for (const p of ring) { x += p[0]; y += p[1]; }
  return [x / ring.length, y / ring.length];
}

export function fatadeDinNiveluri(plans: readonly StoreyPlan[],
                                  amprente: readonly number[][][] = []): Fatada3[] {
  // SUPRATERANELE, după NIVEL, nu după cotă: parterul comercial stă la teren,
  // adică la −0,90 în cadrul documentului (±0,00 e parterul de locuit, pe
  // soclu), și pragul vechi «cota ≥ −0,10» îl arunca cu subsolurile — cortina
  // lui de 5 m nu se desena, iar blocul avea o bandă albă până la etajul 1
  // (Demo, 27.10.2026). Un subsol are nivelul sub 0.
  const cu = plans.filter((p) => Number.isFinite(p.cota) && (p.level ?? 0) >= 0
                                 && (p.outline?.length ?? 0) >= 3);
  // Înălțimea de nivel se ia din PLANȘEUL URMĂTOR al aceluiași corp, nu dintr-o
  // constantă: regimul nu e uniform, iar parterul comercial e mai înalt.
  const peCorp = new Map<string, StoreyPlan[]>();
  for (const p of cu) {
    const k = p.cod ?? "";
    (peCorp.get(k) ?? peCorp.set(k, []).get(k)!).push(p);
  }
  const out: Fatada3[] = [];
  for (const [, lista] of peCorp) {
    lista.sort((a, b) => (a.cota as number) - (b.cota as number));
    lista.forEach((p, i) => {
      const cota = p.cota as number;
      const urm = lista[i + 1];
      const hNivel = urm ? (urm.cota as number) - cota : 3.0;
      const inaltime = Math.max(0.9, Math.min(GOL_MAX, hNivel - PARAPET - 0.35));
      const ring = p.outline;
      // PLĂCILE planșeului: una de obicei; două când o bucată oprită stă între
      // două care urcă. Tot ce se judecă «față de contur» — al cui corp e
      // planșeul, ce gol e pe fațadă, ce logie e scobită — se judecă față de
      // TOATE plăcile, altfel ferestrele de pe placa mică erau «interioare».
      const placi = (p.parts?.length ? p.parts : [ring]).filter((r) => (r?.length ?? 0) >= 3);
      const distLaPlaci = (x: number, y: number) => Math.min(...placi.map((r) => distLaContur(x, y, r)));
      const placaApropiata = (x: number, y: number) =>
        placi.reduce((best, r) => (distLaContur(x, y, r) < distLaContur(x, y, best) ? r : best), placi[0]);
      const inPlaci = (q: number[], tol: number) =>
        placi.some((r) => inPoligon(q[0], q[1], r) || distLaContur(q[0], q[1], r) <= tol);
      // Corpul e cel care CUPRINDE planșeul, nu cel cu același nume. Dar nu
      // după CENTRUL inelului: centrul unui U cade în curte, în afara lui, și
      // planșeele U-ului ajungeau pe alt corp — cu volumul lui nedecupat și
      // neretras, deci cu pielea de fațadă îngropată în el (aripa «albă»).
      // Se ia corpul în care cad cele mai multe colțuri ale planșeului.
      let corp = -1, maxIn = 0;
      amprente.forEach((a, i) => {
        if (a.length < 3) return;
        const n = placi.flat().filter((q) => inPoligon(q[0], q[1], a) || distLaContur(q[0], q[1], a) <= 0.3).length;
        if (n > maxIn) { maxIn = n; corp = i; }
      });
      if (corp < 0) {
        const c = centruInel(ring);
        corp = amprente.findIndex((a) => a.length >= 3 && inPoligon(c[0], c[1], a));
      }
      if (corp < 0) corp = 0;
      const exterioare = (p.spaces ?? [])
        .filter((sp) => /balcon|logg|terrace|terasa|intrare_acoperita/i.test(
          String((sp as { kind?: string }).kind ?? "")))
        .map((sp) => {
          const r = (sp.poly ?? []).filter(
            (q) => Number.isFinite(q?.[0]) && Number.isFinite(q?.[1]));
          const out = (sp as { outdoor?: string }).outdoor;
          // Logia e cea scobită ÎN volum: când documentul n-o spune, se vede
          // din geometrie — toate colțurile ei pe contur sau înăuntru.
          const inauntru = r.length >= 3 && r.every((q) => inPlaci(q, 0.35));
          const terasa = /terrace|terasa/i.test(String((sp as { kind?: string }).kind ?? ""));
          // SCOBITURA INTRĂRII e o logie a parterului: în volum, deschisă spre stradă
          const intrare = String((sp as { kind?: string }).kind ?? "") === "intrare_acoperita";
          return { ring: r, terasa, intrare,
                   logie: terasa || intrare || out === "loggia" || (out !== "balcony" && inauntru),
                   eid: (sp as { eid?: string }).eid ?? "",
                   lumini: (sp as { lumini?: number[][] }).lumini ?? [] };
        })
        .filter((e) => e.ring.length >= 3);
      const balcoane = exterioare.filter((e) => !e.logie).map((e) => e.ring);
      const balcoaneEid = exterioare.filter((e) => !e.logie).map((e) => e.eid);
      const copertine = (p.slabs ?? [])
        .filter((sl) => sl.kind === "cantilever" && (sl.poly?.length ?? 0) >= 3)
        .map((sl) => ({ poly: sl.poly, cota: Number.isFinite(sl.sill_m) ? (sl.sill_m as number) : 0,
                        // COPERTINA INTRĂRII (3.19): de beton sau de metal, cu grosimea ei
                        ...(sl.rol === "copertina_intrare"
                          ? { structura: sl.structura ?? "beton",
                              ...(Number.isFinite(sl.thickness_m) ? { grosime: sl.thickness_m as number } : {}) }
                          : {}) }));
      const denivelate = (p.slabs ?? [])
        .filter((sl) => sl.kind === "offset" && (sl.poly?.length ?? 0) >= 3 && Number.isFinite(sl.sill_m))
        .map((sl) => ({ poly: sl.poly, cota: sl.sill_m as number, eid: sl.eid }));
      const trepte = (p.stairs ?? [])
        .filter((sc) => (sc.poly?.length ?? 0) >= 3 && (sc.height_m ?? 0) > 0)
        .map((sc) => ({ poly: sc.poly, deg: sc.deg ?? 0, cota0: sc.sill_m ?? 0, h: sc.height_m as number,
                        contratrepte: Math.max(1, Math.round((sc.height_m as number) / 0.15)), eid: sc.eid }));
      const platforme = (p.lifts ?? [])
        .filter((lf) => (lf.poly?.length ?? 0) >= 3 && (lf.height_m ?? 0) > 0)
        .map((lf) => ({ poly: lf.poly, cota0: lf.sill_m ?? 0, h: lf.height_m as number, eid: lf.eid }));
      const logii = exterioare.filter((e) => e.logie).map((e) => e.ring);
      const intrari = exterioare.filter((e) => e.intrare).map((e) => e.ring);
      // SPOTURILE INTRĂRII: sub tavanul scobiturii (fața de jos a intradosului
      // din dreptul lor; sub placă, fără el) și sub copertină — la metal, sub
      // cadrul ei, la beton tot sub intradosul ei
      const subTavan = (x: number, y: number, implicit: number) => {
        const z = (p.panouri ?? []).filter((pn) => pn.surface === "soffit" && (pn.poly?.length ?? 0) >= 3
                                           && inPoligon(x, y, pn.poly as number[][]))
          .map((pn) => pn.z0);
        return z.length ? Math.min(...z) : implicit;
      };
      const lumini: { x: number; y: number; z: number }[] = [];
      for (const e of exterioare.filter((x) => x.intrare)) {
        for (const [x, y] of e.lumini) lumini.push({ x, y, z: subTavan(x, y, hNivel - 0.22) });
      }
      for (const sl of (p.slabs ?? []).filter((x) => x.rol === "copertina_intrare")) {
        const jos = (Number.isFinite(sl.sill_m) ? (sl.sill_m as number) : 3.0) - (sl.thickness_m ?? 0.22);
        for (const [x, y] of sl.lumini ?? []) {
          lumini.push({ x, y, z: sl.structura === "metal" ? jos : subTavan(x, y, jos) });
        }
      }
      const terase = exterioare.filter((e) => e.terasa).map((e) => e.ring);
      const goluri: GolFatada[] = [];
      for (const o of p.openings ?? []) {
        // Ferestrele, și ușile care DAU AFARĂ: o ușă de balcon sau de terasă e
        // un gol de fațadă ca oricare, cu tâmplăria ei. Se recunosc după
        // tâmplăria scrisă pe ele de faza de fațadă (`canate`/`profil`/
        // `culoare_toc`): numai golurile care au căzut pe o față o primesc,
        // restul sunt uși interioare și n-au ce căuta în anvelopă.
        if (o.tip !== "Window" && o.canate === undefined
            && o.profil_m === undefined && o.culoare_toc === undefined) continue;
        // golul din beton din spatele cortinei: îl vitrează cortina
        if (o.inCortina) continue;
        const s = segmentulGolului(o);
        if (!s) continue;
        const mx = (s.a[0] + s.b[0]) / 2, my = (s.a[1] + s.b[1]) / 2;
        // PE CONTUR, sau PE PERETELE UNEI LOGII/TERASE: fereastra din fundul
        // unei logii stă la 1,3 m de contur, cea din fundul unei terase de capăt
        // la 4,4 m — după contur singur, amândouă erau «goluri interioare» și
        // fundul rămânea orb. Normala e spre GOLUL logiei (afară din perete),
        // adică inversul normalei exterioare a inelului ei.
        const Lg = Math.hypot(s.b[0] - s.a[0], s.b[1] - s.a[1]) || 1;
        const dirGol: Pt = [(s.b[0] - s.a[0]) / Lg, (s.b[1] - s.a[1]) / Lg];
        // CONTURUL SAU LOGIA — CINE E MAI APROAPE DE GOL, măsurat pe latura
        // PARALELĂ cu el. Luat conturul întâi de tot ce stă la sub 0,9 m de el,
        // ușa din fundul logiei de colț (la 16 cm de fața vecină, care e
        // perpendiculară pe ea) se lipea de latura paralelă a conturului aflată
        // la 1,3 m și ieșea desenată în gura logiei, în loc de peretele ei
        // (U-ul din Test-Catalin-v1, 29.09.2026).
        type Lat = { p: Pt; u: Pt; n: Pt; d: number };
        let lat: Lat | null = null;
        const dRing = distLaPlaci(mx, my);
        if (dRing <= LANGA_CONTUR) {
          const l1 = laturaApropiata(mx, my, placaApropiata(mx, my), dirGol);
          if (l1) lat = { p: l1.p, u: l1.u, n: l1.n, d: distLaSegmentDreapta(mx, my, l1.p, l1.u) };
        }
        for (const lg of logii) {
          if (distLaContur(mx, my, lg) > LANGA_CONTUR) continue;
          const l2 = laturaApropiata(mx, my, lg, dirGol);
          if (!l2) continue;
          const d2 = distLaSegmentDreapta(mx, my, l2.p, l2.u);
          if (!lat || d2 < lat.d) lat = { p: l2.p, u: l2.u, n: [-l2.n[0], -l2.n[1]], d: d2 };
        }
        if (!lat) continue;
        const w = Math.hypot(s.b[0] - s.a[0], s.b[1] - s.a[1]) || 1.2;
        goluri.push({
          a: [lat.p[0] - lat.u[0] * w / 2, lat.p[1] - lat.u[1] * w / 2],
          b: [lat.p[0] + lat.u[0] * w / 2, lat.p[1] + lat.u[1] * w / 2],
          // Cotele DIN DOCUMENT când le are (măsurate sau puse ca implicite
          // de emitent, cu proveniența spusă); convenția machetei doar când
          // lipsesc.
          parapet: Number.isFinite(o.sill_m) ? (o.sill_m as number) : PARAPET,
          inaltime: Number.isFinite(o.height_m) && (o.height_m as number) > 0
            ? (o.height_m as number) : inaltime,
          n: lat.n,
          tip: o.tip,
          canate: o.canate, profil: o.profil_m, culoare: o.culoare_toc,
          tamplarie: o.tamplarie, balama: o.balama, eid: o.eid,
          ...(o.colt ? { coltCu: o.colt } : {}),
        });
      }
      // Panourile: normala vine de pe `Facade`; când lipsește se ia din
      // latura de contur cea mai apropiată de mijlocul panoului.
      const panouri: PanouFatada[] = (p.panouri ?? []).map((pn) => {
        if (pn.n && pn.n.length >= 2) return pn;
        const mx = (pn.a[0] + pn.b[0]) / 2, my = (pn.a[1] + pn.b[1]) / 2;
        const lat = laturaApropiata(mx, my, placaApropiata(mx, my));
        return lat ? { ...pn, n: lat.n } : pn;
      });
      const parapete: ParapetFatada[] = p.parapete ?? [];
      // Balcoanele nivelului: geometrie reală, nu o ieșitură desenată din ochi.
      // Logia e ALTCEVA decât balconul: nu iese din corp, intră în el.
      const acoperis = String(p.kind ?? "") === "roof";
      // Pereții `parapet` ai nivelului sunt de două feluri: ATICUL (stă pe
      // planșeul de deasupra: `sill_m` = înălțimea nivelului) și parapetele
      // de la pardoseală din jurul teraselor (fără `sill_m`, ~1 m). Desenate
      // toate ca atic, cele din urmă ieșeau grinzi albe în aer peste terase.
      const faraDubluri = (r: number[][]) => {
        const out: number[][] = [];
        for (const q of r) {
          const u = out[out.length - 1];
          if (u && Math.hypot(q[0] - u[0], q[1] - u[1]) < 0.02) continue;
          out.push(q);
        }
        while (out.length > 1 && Math.hypot(out[0][0] - out[out.length - 1][0], out[0][1] - out[out.length - 1][1]) < 0.02) out.pop();
        return out;
      };
      // …ȘI CEI DE DEASUPRA TĂIETURII: aticul are `sill_m` = înălțimea
      // nivelului, deci planșa îl scoate din `walls` (v. `peste_sectiune`) și
      // ajunge în `wallsAboveCut`. Citit doar din `walls`, inelul lui lipsea,
      // macheta cădea pe amprenta întreagă a corpului și ridica atic și în
      // jurul teraselor retrase.
      const parapeti = [...(p.walls ?? []), ...(p.wallsAboveCut ?? [])]
        .filter((w) => String((w as { kind?: string }).kind ?? "") === "parapet")
        .map((w) => ({ poly: faraDubluri((w.poly ?? []).filter((q) => Number.isFinite(q?.[0]))),
                       holes: ((w as { holes?: number[][][] }).holes ?? []).filter((h) => h.length >= 3),
                       sill: Number.isFinite(w.sill_m) ? (w.sill_m as number) : 0,
                       h: Number.isFinite(w.height_m) && (w.height_m as number) > 0 ? (w.height_m as number) : 1.0 }))
        .filter((r) => r.poly.length >= 3);
      const atic_inele: Suprafata[] = parapeti.filter((r) => r.sill >= 1)
        .map((r) => ({ poly: r.poly, holes: r.holes }));
      const parapete_zid = parapeti.filter((r) => r.sill < 1)
        .map((r) => ({ poly: r.poly, holes: r.holes, h: r.h }));
      // Aticul stă pe ULTIMUL etaj, cu `z` peste înălțimea lui (v. `layout.fatada`).
      // …din PIEILE lui, nu din glaf: glaful urcă peste creastă (`GLAF_PESTE_M`),
      // iar zidul ridicat până la fața lui de sus ieșea alb pe lângă tablă și se
      // bătea cu ea în același plan (Cătălin, 8.10.2026: «aticul nu e acoperit»).
      const hAtic = Math.max(0, ...panouri
        .filter((pn) => pn.surface === "parapet" && pn.kind !== "sill").map((pn) => pn.z1 - hNivel));
      // Un nivel intră în listă chiar fără goluri: muchia de planșeu se vede
      // oricum, și ea e cea care dă ritmul orizontal al unei fațade.
      if (goluri.length || balcoane.length || amprente.length || panouri.length) {
        out.push({ corp, cota, storey: p.id, nivel: p.level, balcoaneEid,
                   contur: ring, ...(placi.length >= 2 ? { parti: placi } : {}),
                   ...(copertine.length ? { copertine } : {}),
                   ...(intrari.length ? { intrari } : {}),
                   ...(lumini.length ? { lumini } : {}),
                   ...(denivelate.length ? { denivelate } : {}),
                   ...(trepte.length ? { trepte } : {}),
                   ...(platforme.length ? { platforme } : {}),
                   goluri, balcoane, logii, terase, panouri, parapete, acoperis,
                   atic: hAtic, inaltime: acoperis ? 0 : hNivel, atic_inele, parapete_zid });
      }
    });
  }
  return out;
}
